import { Component, OnDestroy, OnInit } from '@angular/core';
import moment from 'moment-timezone';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import { UsersService } from 'src/app/_services/admin/users.service';
import { ErrorUtil } from 'src/app/_utilities/error';

@Component({
  selector: 'app-manage-working-hours',
  standalone: false,
  templateUrl: './manage-working-hours.component.html',
  styleUrl: './manage-working-hours.component.scss'
})
export class ManageWorkingHoursComponent extends SubscriptionUtil implements OnInit, OnDestroy {
  translate: any;
  data: any;
  editForm: boolean = true;
  timezone: any;
  company: any = { calendar: {} };
  saving: boolean = false;

  workingDays = [
    { key: 'monday', labelKey: 'monday', workDay: 'MONDAY' },
    { key: 'tuesday', labelKey: 'tuesday', workDay: 'TUESDAY' },
    { key: 'wednesday', labelKey: 'wednesday', workDay: 'WEDNESDAY' },
    { key: 'thursday', labelKey: 'thursday', workDay: 'THURSDAY' },
    { key: 'friday', labelKey: 'friday', workDay: 'FRIDAY' },
    { key: 'saturday', labelKey: 'saturday', workDay: 'SATURDAY' },
    { key: 'sunday', labelKey: 'sunday', workDay: 'SUNDAY' }
  ];

  constructor(
    private languageTranslateService: LanguageTranslateService,
    public modalRef: BsModalRef,
    public auth: AuthenticationService,
    private userService: UsersService,
    private errorUtil: ErrorUtil,
  ) {
    super();
  }

  ngOnInit(): void {
    this.timezone = this.auth.getUserTimezone();
    this.getLanguageData();
    this.initCalendar();
  }

  private emptyCalendar() {
    return {
      monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false,
      mondayAllDay: false, tuesdayAllDay: false, wednesdayAllDay: false, thursdayAllDay: false, fridayAllDay: false, saturdayAllDay: false, sundayAllDay: false,
      mondayStart: null, mondayEnd: null, tuesdayStart: null, tuesdayEnd: null, wednesdayStart: null, wednesdayEnd: null,
      thursdayStart: null, thursdayEnd: null, fridayStart: null, fridayEnd: null, saturdayStart: null, saturdayEnd: null,
      sundayStart: null, sundayEnd: null,
    };
  }

  private initCalendar() {
    const profile = this.data;
    this.company.calendar = this.emptyCalendar();

    if (!profile?.userCalendar) {
      return;
    }

    // Already transformed object (from user-details-v2 getCalendarData)
    if (!Array.isArray(profile.userCalendar) && typeof profile.userCalendar === 'object') {
      this.company.calendar = { ...this.emptyCalendar(), ...profile.userCalendar };
      return;
    }

    // API array format
    if (Array.isArray(profile.userCalendar)) {
      this.company.calendar = this.mapCalendarFromApi(profile.userCalendar);
    }
  }

  private mapCalendarFromApi(userCalendar: any[]) {
    const calendar: any = this.emptyCalendar();
    for (let j = 0; j < userCalendar.length; j++) {
      let startWorkHour: any = null;
      let endWorkHour: any = null;
      if (userCalendar[j].startWorkHour && userCalendar[j].endWorkHour) {
        const startMomentTime = moment(moment().format('MM-DD-YYYY') + ' ' + userCalendar[j].startWorkHour);
        const startMoment2 = moment.tz(startMomentTime, this.auth.getUserTimezone());
        startWorkHour = new Date(startMoment2.toDate());

        const endMomentTime = moment(moment().format('MM-DD-YYYY') + ' ' + userCalendar[j].endWorkHour);
        endWorkHour = new Date(endMomentTime.toDate());
      }

      const day = (userCalendar[j].workDay || '').toLowerCase();
      if (!day) {
        continue;
      }
      calendar[day] = true;
      calendar[day + 'Start'] = startWorkHour;
      calendar[day + 'End'] = endWorkHour;
      if (!startWorkHour || !endWorkHour || userCalendar[j].allDay) {
        calendar[day + 'AllDay'] = true;
      }
    }
    return calendar;
  }

  selectWorkingDays(workDay: any, val: any) {
    const startMomentTime = moment(moment().format('MM-DD-YYYY') + ' 09:00');
    const startMoment2 = moment.tz(startMomentTime, this.auth.getUserTimezone());
    const startMoment = new Date(startMoment2.toDate());

    const pickUpstartWorkHour = moment(moment().format('MM-DD-YYYY') + ' 17:00');
    const endMoment2 = moment.tz(pickUpstartWorkHour, this.auth.getUserTimezone());
    const endMoment = new Date(endMoment2.toDate());

    this.company.calendar[workDay] = val;
    if (val) {
      this.company.calendar[workDay + 'Start'] = startMoment;
      this.company.calendar[workDay + 'End'] = endMoment;
    } else {
      this.company.calendar[workDay + 'Start'] = null;
      this.company.calendar[workDay + 'End'] = null;
      this.company.calendar[workDay + 'AllDay'] = false;
    }
  }

  nullify(day: any, val: any) {
    const startMomentTime = moment(moment().format('MM-DD-YYYY') + ' 09:00');
    const startMoment2 = moment.tz(startMomentTime, this.auth.getUserTimezone());
    const startMoment = new Date(startMoment2.toDate());

    const pickUpstartWorkHour = moment(moment().format('MM-DD-YYYY') + ' 17:00');
    const endMoment2 = moment.tz(pickUpstartWorkHour, this.auth.getUserTimezone());
    const endMoment = new Date(endMoment2.toDate());

    if (val) {
      this.company.calendar[day + 'Start'] = null;
      this.company.calendar[day + 'End'] = null;
    } else {
      this.company.calendar[day + 'Start'] = startMoment;
      this.company.calendar[day + 'End'] = endMoment;
    }
  }

  private buildUserCalendarPayload(): any[] {
    const cal = this.company.calendar || {};
    const userCalendar: any[] = [];

    this.workingDays.forEach((d) => {
      if (cal[d.key] && ((cal[d.key + 'Start'] && cal[d.key + 'End']) || cal[d.key + 'AllDay'])) {
        userCalendar.push({
          startWorkHour: cal[d.key + 'Start'] == null ? null : moment(cal[d.key + 'Start']).format('HH:mm'),
          endWorkHour: cal[d.key + 'End'] == null ? null : moment(cal[d.key + 'End']).format('HH:mm'),
          allDay: !!cal[d.key + 'AllDay'],
          workDay: d.workDay,
        });
      }
    });

    return userCalendar;
  }

  onSave() {
    const userId = this.data?.id;
    if (!userId || this.saving) {
      return;
    }

    this.saving = true;
    this.push(
      this.userService.getUserProfile(userId).subscribe((latestProfile: any) => {
        const formData: any = {
          ...latestProfile,
         // shiftWorking: '0',
          userCalendar: this.buildUserCalendarPayload(),
          isUserCalenderUpdated: true,
        };

        if (formData.geoFence?.id) {
          formData.geoFence = { id: formData.geoFence.id };
        } else {
          formData.geoFence = null;
        }

        if (formData.defaultDepot?.id != null) {
          formData.defaultDepot = { id: formData.defaultDepot.id };
        } else {
          formData.defaultDepot = null;
        }

        if (Array.isArray(formData.userDynamicSkillList)) {
          formData.userDynamicSkillList = formData.userDynamicSkillList.map((s: any) => {
            const copy = { ...s };
            delete copy.label;
            return copy;
          });
        }

        this.push(
          this.userService.editUserProfile(formData, userId).subscribe((res: any) => {
            this.saving = false;
            if (res?.status === 200 || res?.status === 201) {
              this.errorUtil.setErrorMessage(
                200,
                this.translate?.general.messages.updatedSuccessfully,
                null,
                'success',
                1000
              );
              if (res.body?.shiftWorking != null) {
                localStorage.setItem('shiftWorking', res.body.shiftWorking);
              }
              this.userService.refreshList$.emit(res);
              this.modalRef.hide();
            } else {
              this.errorUtil.setErrorMessage(
                res?.status || 400,
                null,
                res?.error?.title || res?.error?.message || null,
                'error',
                3000
              );
            }
          })
        );
      })
    );
  }

  /********************************** Start Language **************************/
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
        }
      })
    );
  }
  /********************************** End Language **************************/

  push(obs: any) {
    super.push(obs);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
